import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  Typography,
  Paper,
  Divider,
  CircularProgress,
  useTheme,
  Grid
} from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { authenticationService } from "src/services";
import { get, post } from 'src/services/httpClient.service';
import { useWalletContext } from 'src/components/walletcontexthook';
import { getResponsiveStyles, isMobileScreen } from 'src/common/utils/responsive';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';

// Define interfaces for your data structures
interface PaymentData {
  amount: string;
  currency: string;
  paymentDate: string;
  paymentGateway: string;
  paymentMethod: string;
  paymentMode: string;
  paymentStatus: string;
  transactionId: string;
  transactionType: string;
  chequeNumber: string;
  ddNumber: string;
  franchiseId: string;
  refundAmount: string;
  refundId: string;
  companyId: string;
}

interface Franchise {
  id: string;
  name: string;
  status: boolean;
  phone?: string;
  email?: string;
  vendorId?: string;
  companyId?: string;
}

interface AddPaymentProps {
  handleCloseDialog: () => void;
  fetchData: () => void;
}

interface Errors {
  amount: boolean;
  currency: boolean;
  paymentDate: boolean;
  paymentGateway: boolean;
  paymentMethod: boolean;
  paymentMode: boolean;
  paymentStatus: boolean;
  transactionId: boolean;
  franchiseId: boolean;
  transactionType: boolean;
  chequeNumber: boolean,
  ddNumber: boolean,
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddFranchisePayment: React.FC<AddPaymentProps> = ({ handleCloseDialog, fetchData }) => {
  const theme = useTheme();
  const styles = getResponsiveStyles(theme);
  const [isMobile, setIsMobile] = useState(isMobileScreen(theme));
  const { showSnackbar } = useSnackbar();
  
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();
  const accountType = localStorage.getItem('accountType');
  const { walletAmount, fetchOperatorDetails } = useWalletContext();
  
  // Initialize form state with proper typing
  const [paymentData, setPaymentData] = useState<PaymentData>({
    amount: '',
    currency: 'INR',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentGateway: '',
    paymentMethod: '',
    paymentMode: 'ONLINE',
    paymentStatus: 'SUCCESS',
    transactionId: '',
    franchiseId: '',
    refundAmount: '',
    refundId: '',
    companyId: '',
    transactionType: 'CREDIT',
    chequeNumber: '',
    ddNumber: '',
  });

  const [errors, setErrors] = useState<Errors>({
    amount: false,
    currency: false,
    paymentDate: false,
    paymentGateway: false,
    paymentMethod: false,
    paymentMode: false,
    paymentStatus: false,
    transactionId: false,
    franchiseId: false,
    transactionType: false,
    chequeNumber: false,
    ddNumber: false
  });

  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const API_URL = process.env.REACT_APP_BASE_API_URL;
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileScreen(theme));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [theme]);

  const validateForm = (): boolean => {
    let formIsValid = true;
    const newErrors: Errors = {
      amount: false,
      currency: false,
      paymentDate: false,
      paymentGateway: false,
      paymentMethod: false,
      paymentMode: false,
      paymentStatus: false,
      transactionId: false,
      franchiseId: false,
      transactionType: false,
      chequeNumber: false,
      ddNumber: false,
    };

    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      newErrors.amount = true;
      formIsValid = false;
    }

    if (!paymentData.currency) {
      newErrors.currency = true;
      formIsValid = false;
    }

    if (!paymentData.paymentDate) {
      newErrors.paymentDate = true;
      formIsValid = false;
    }

    // if (!paymentData.paymentGateway) {
    //   newErrors.paymentGateway = true;
    //   formIsValid = false;
    // }
    if (
      paymentData.paymentMode === 'ONLINE' &&
      !paymentData.paymentGateway
    ) {
      newErrors.paymentGateway = true;
      formIsValid = false;
    }

    if (!paymentData.paymentMethod) {
      newErrors.paymentMethod = true;
      formIsValid = false;
    }

    if (!paymentData.paymentMode) {
      newErrors.paymentMode = true;
      formIsValid = false;
    }

    if (!paymentData.paymentStatus) {
      newErrors.paymentStatus = true;
      formIsValid = false;
    }

    // ONLINE payments -> Transaction ID required
    if (
      paymentData.paymentMode === 'ONLINE' &&
      !paymentData.transactionId.trim()
    ) {
      newErrors.transactionId = true;
      formIsValid = false;
    }

    // OFFLINE BANK TRANSFER -> Transaction ID optional
    // OFFLINE CASH -> Transaction ID not required

    // OFFLINE CHEQUE -> Cheque Number required
    if (
      paymentData.paymentMode === 'OFFLINE' &&
      paymentData.paymentMethod === 'Cheque' &&
      !paymentData.chequeNumber.trim()
    ) {
      newErrors.chequeNumber = true;
      formIsValid = false;
    }

    // OFFLINE DD -> DD Number required
    if (
      paymentData.paymentMode === 'OFFLINE' &&
      paymentData.paymentMethod === 'DD' &&
      !paymentData.ddNumber.trim()
    ) {
      newErrors.ddNumber = true;
      formIsValid = false;
    }

    if (!paymentData.transactionType) {
      newErrors.transactionType = true;
      formIsValid = false;
    }

    if (!paymentData.franchiseId) {
      newErrors.franchiseId = true;
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const fetchFranchiseList = async () => {
    try {
      let response = null;

      if (accountType?.trim() === "OWNER") {
        response = await get(`${API_URL}/api/franchisees/vendor/${vendorId}/fetchAll`);
        console.log("Owner Franchise list:", response);
        setFranchises(Array.isArray(response.data) ? response.data : []);
      } else {
        response = await get(`${API_URL}/api/franchisees/vendor/${vendorId}/getFranchiseeById?id=${franchiseId}`);
        console.log("Single Franchise:", response);
        setFranchises(response.data ? [response.data] : []);
      }
    } catch (err) {
      console.error("Error fetching franchises:", err);
    }
  };

  useEffect(() => {
    fetchFranchiseList();
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    setPaymentData(prev => ({
      ...prev,
      [name as string]: value as string,

      ...(name === 'paymentMode'
        ? {
            paymentMethod: '',
            paymentGateway: '',
            transactionId: '',
            chequeNumber: '',
            ddNumber: ''
          }
        : {}),

      ...(name === 'paymentMethod'
        ? {
            transactionId: '',
            chequeNumber: '',
            ddNumber: ''
          }
        : {})
    }));

    if (errors[name as keyof Errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }

    if (name === 'paymentMode') {
      setErrors(prev => ({
        ...prev,
        paymentGateway: false,
        paymentMethod: false,
        transactionId: false
      }));
    }

    if (name === 'paymentMethod') {
      setErrors(prev => ({
        ...prev,
        transactionId: false
      }));
    }
    /*
    const { name, value } = e.target;
    setPaymentData(prev => ({ ...prev, [name as string]: value as string }));
    
    // Clear error for this field
    if (errors[name as keyof Errors]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }

    if (name === "franchiseId") {
      const selected = franchises.find(f => f.id === value);

      if (selected) {
        setPaymentData(prev => ({
          ...prev,
          franchiseId: selected.id,
          companyId: selected.companyId || "",
          paymentGateway: "RazorPay",
        }));
      }
    }
    */
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showSnackbar('Please fill all fields correctly.', 'error');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...paymentData,
        vendorId: vendorId,
        franchiseId: paymentData.franchiseId,
        companyId: paymentData.companyId,
        paymentDate: new Date(paymentData.paymentDate).toISOString(),
        amount: Number(paymentData.amount) || 0,
        refundAmount: Number(paymentData.refundAmount) || 0,
        paymentMode: paymentData.paymentMode,
      };

      console.log("Final Payload → ", payload);

      const response = await post(`${API_URL}/api/franchise-payments`, payload);

      if (response?.responseCode == 200 || response?.responseCode == 201) {
        showSnackbar(response?.responseMessage || 'Payment added successfully!', 'success');
        fetchData();
        setTimeout(() => handleCloseDialog(), 1200);
      } else {
        showSnackbar(response?.responseMessage || 'Failed to add payment', 'error');
      }
    } catch (error: any) {
      console.error('Error adding payment:', error);
      showSnackbar(error?.message || 'Failed to add payment. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const paymentModes = ['ONLINE', 'OFFLINE'];
  const transactionTypes = ['CREDIT', 'DEBIT'];
  //const paymentMethods = ['Credit Card', 'Debit Card', 'UPI', 'Net Banking', 'Wallet'];
  const paymentGateways = ['RazorPay', 'PhonePe', 'GPay', 'CashFree', 'PayTm', 'PayU', 'PayPal', 'CCAvenue', 'Stripe'];
  const paymentStatuses = ['SUCCESS']; //, 'PENDING', 'CREATED', 'FAILED', 'REFUNDED'

  const onlinePaymentMethods = [
    'UPI',
    'Credit Card',
    'Debit Card',
    'Net Banking',
    'Wallet'
  ];

  const offlinePaymentMethods = [
    'Cash',
    'Bank Transfer',
    'Cheque',
    'DD'
  ];

  const paymentMethods =
    paymentData.paymentMode === 'ONLINE'
      ? onlinePaymentMethods
      : offlinePaymentMethods;

  return (
    <>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, maxHeight: '80vh', overflow: 'auto' }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 2,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              pb: 1,
              display: 'inline-block'
            }}
          >
            Add Franchise Payment
          </Typography>

       

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Franchise Dropdown */}
            <Grid item xs={12} sm={12}>
              <FormControl fullWidth error={errors.franchiseId}>
                <InputLabel>Franchise *</InputLabel>
                <Select
                  name="franchiseId"
                  label="Franchise *"
                  value={paymentData.franchiseId}
                  onChange={handleChange}
                  disabled={loading}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {franchises.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {`${f.name} - ${f.status ? "ACTIVE" : "INACTIVE"}`}
                    </MenuItem>
                  ))}
                </Select>
                {errors.franchiseId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    Franchise is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Amount Field */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Amount *"
                name="amount"
                type="number"
                fullWidth
                value={paymentData.amount}
                onChange={handleChange}
                error={errors.amount}
                helperText={errors.amount ? 'Amount is required and must be greater than 0.' : ''}
                disabled={loading}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>,
                }}
              />
            </Grid>
            
            {/* Currency */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth error={errors.currency}>
                <InputLabel>Currency *</InputLabel>
                <Select
                  name="currency"
                  value={paymentData.currency}
                  onChange={handleChange}
                  label="Currency *"
                  disabled={loading}
                >
                  <MenuItem value="INR">INR (Indian Rupee)</MenuItem>
                  <MenuItem value="USD">USD (US Dollar)</MenuItem>
                  <MenuItem value="EUR">EUR (Euro)</MenuItem>
                  <MenuItem value="GBP">GBP (British Pound)</MenuItem>
                </Select>
                {errors.currency && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    Currency is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth error={errors.transactionType}>
                <InputLabel>Transaction Type *</InputLabel>
                <Select
                  name="transactionType"
                  value={paymentData.transactionType}
                  onChange={handleChange}
                  label="Payment Mode *"
                  disabled={loading}
                >
                  {transactionTypes.map((mode) => (
                    <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                  ))}
                </Select>
                {errors.transactionType && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    Transaction Type is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Payment Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment Date *"
                name="paymentDate"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={paymentData.paymentDate}
                onChange={handleChange}
                error={errors.paymentDate}
                helperText={errors.paymentDate ? 'Payment date is required.' : ''}
                disabled={loading}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>

            {/* Payment Mode Dropdown */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={errors.paymentMode}>
                <InputLabel>Payment Mode *</InputLabel>
                <Select
                  name="paymentMode"
                  value={paymentData.paymentMode}
                  onChange={handleChange}
                  label="Payment Mode *"
                  disabled={loading}
                >
                  {paymentModes.map((mode) => (
                    <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                  ))}
                </Select>
                {errors.paymentMode && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    Payment mode is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Payment Method Dropdown */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={errors.paymentMethod}>
                <InputLabel>Payment Method *</InputLabel>
                <Select
                  name="paymentMethod"
                  value={paymentData.paymentMethod}
                  onChange={handleChange}
                  label="Payment Method *"
                  disabled={loading}
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method} value={method}>{method}</MenuItem>
                  ))}
                </Select>
                {errors.paymentMethod && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    Payment method is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Payment Gateway Dropdown */}
            { paymentData.paymentMode === 'ONLINE' && (<Grid item xs={12} sm={6}>
              <FormControl fullWidth error={errors.paymentGateway}>
                <InputLabel>Payment Gateway *</InputLabel>
                <Select
                  name="paymentGateway"
                  value={paymentData.paymentGateway}
                  onChange={handleChange}
                  label="Payment Gateway *"
                  disabled={loading}
                >
                  <MenuItem value="">Select Gateway</MenuItem>
                  {paymentGateways.map((gateway) => (
                    <MenuItem key={gateway} value={gateway}>{gateway}</MenuItem>
                  ))}
                </Select>
                {errors.paymentGateway && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    Payment gateway is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>)}

            

            {/* Transaction ID */}
            {/* <Grid item xs={12} sm={6}>
              <TextField
                label="Transaction ID *"
                name="transactionId"
                fullWidth
                value={paymentData.transactionId}
                onChange={handleChange}
                error={errors.transactionId}
                helperText={errors.transactionId ? 'Transaction ID is required.' : ''}
                disabled={loading}
                size={isMobile ? "small" : "medium"}
              />
            </Grid> */}

            {/* Transaction ID */}
                        { (paymentData.paymentMode === 'ONLINE' || (paymentData.paymentMode === 'OFFLINE' &&
                                  paymentData.paymentMethod === 'Bank Transfer')) && (<Grid item xs={12} sm={6}>
                          <TextField
                            label={`Transaction ID${
                              paymentData.paymentMode === 'ONLINE' ? ' *' : ''
                            }`}
                            name="transactionId"
                            fullWidth
                            value={paymentData.transactionId}
                            onChange={handleChange}
                            error={errors.transactionId}
                            helperText={
                              errors.transactionId
                                ? 'Transaction ID is required'
                                : paymentData.paymentMode === 'OFFLINE' &&
                                  paymentData.paymentMethod === 'Bank Transfer'
                                ? 'Transaction ID is optional.'
                                : ''
                            }
                            disabled={
                              loading ||
                              (paymentData.paymentMode === 'OFFLINE' &&
                                paymentData.paymentMethod !== 'Bank Transfer')
                            }
                            size={isMobile ? "small" : "medium"}
                          />
                        </Grid>
                      )}
                        
                        {paymentData.paymentMode === 'OFFLINE' &&
                          paymentData.paymentMethod === 'Cheque' && (
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Cheque Number *"
                                name="chequeNumber"
                                fullWidth
                                value={paymentData.chequeNumber}
                                onChange={handleChange}
                                error={errors.chequeNumber}
                                helperText={
                                  errors.chequeNumber
                                    ? 'Cheque Number is required.'
                                    : ''
                                }
                                disabled={loading}
                                size={isMobile ? "small" : "medium"}
                              />
                            </Grid>
                          )}
            
                        {paymentData.paymentMode === 'OFFLINE' &&
                          paymentData.paymentMethod === 'DD' && (
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="DD Number *"
                                name="ddNumber"
                                fullWidth
                                value={paymentData.ddNumber}
                                onChange={handleChange}
                                error={errors.ddNumber}
                                helperText={
                                  errors.ddNumber
                                    ? 'DD Number is required.'
                                    : ''
                                }
                                disabled={loading}
                                size={isMobile ? "small" : "medium"}
                              />
                            </Grid>
                          )}

            

            {/* Payment Status Dropdown */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={errors.paymentStatus}>
                <InputLabel>Payment Status *</InputLabel>
                <Select
                  name="paymentStatus"
                  value={paymentData.paymentStatus}
                  onChange={handleChange}
                  label="Payment Status *"
                  disabled={loading}
                >
                  {paymentStatuses.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
                {errors.paymentStatus && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    Payment status is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            

          </Grid>

          {/* Buttons */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 2, 
              mt: 4,
              flexDirection: isMobile ? 'column-reverse' : 'row'
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCloseDialog}
              disabled={loading}
              fullWidth={isMobile}
              sx={{ py: isMobile ? 1 : 1.5 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              fullWidth={isMobile}
              sx={{ py: isMobile ? 1 : 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit Payment'}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
            * Required fields
          </Typography>
        </Box>
      </Paper>
    </>
  );
};

export default AddFranchisePayment;